<template>
  <div>
    <div v-if="isLoading" class="overlay">
      <loader />
    </div>
    <div class="fixed-change-order-actions" :style="navBarStyle">
      <!-- Progress bar - only for quote flow (dealer) -->
      <change-order-status-progress v-if="hasQuoteId" />

      <div v-if="hasQuoteId" class="change-order-action-buttons__wrapper">
        <!-- Quote flow layout -->
        <div class="column">
          <div>
            <app-button
              v-if="hasSuccessTryOrderResult"
              data-qa="try-change-order-button"
              :disabled="tryOrderDisabled"
              @click="tryOrder"
              >{{ i18n.t('/change-order/actions/try-order') }}
            </app-button>

            <app-button
              v-if="isSigningStatusCompleted || isChangeOrderStatusSubmitted"
              data-qa="save-change-order-button"
              @click="showPrintOrderDialogue"
              >{{ i18n.t('/change-order/actions/print-pdf-dialogue-button') }}
            </app-button>
          </div>
        </div>
        <div class="column">
          <div class="action-buttons">
            <!-- PDF dialogue button -->
            <app-button
              v-if="canShowPDFButton"
              data-qa="save-change-order-button"
              @click="showPrintOrderDialogue"
              >{{ i18n.t('/change-order/actions/print-pdf-dialogue-button') }}
            </app-button>

            <!-- try order button -->
            <app-button
              v-if="canShowTryOrderButton"
              data-qa="try-change-order-button"
              :disabled="tryOrderDisabled"
              @click="tryOrder"
              >{{ i18n.t('/change-order/actions/try-order') }}
            </app-button>

            <!-- submit change order -->
            <app-button
              v-if="canShowChangeOrderButton"
              :disabled="sendOrderNadinDisabled"
              data-qa="send-change-order-button"
              @click="sendOrder"
              >{{ i18n.t('/change-order/actions/send-order') }}
            </app-button>
          </div>
        </div>
      </div>

      <div v-else class="action-buttons">
        <!-- Generic flow layout (importer) -->
        <!-- temporarily hide the button -->
        <app-button
          v-if="false"
          :disabled="isSaveButtonDisabled"
          data-qa="save-change-order-button"
          @click="saveChangeOrder"
          >{{ i18n.t('/Common/Save') }}
        </app-button>
        <div>
          <app-button
            v-if="!orderIsSubmitted"
            data-qa="try-change-order-button"
            :disabled="tryOrderDisabled"
            @click="tryOrder"
            >{{ i18n.t('/change-order/actions/try-order') }}
          </app-button>
        </div>
        <div>
          <app-button
            v-if="orderIsSubmitted"
            data-qa="new-change-order-button"
            @click="createNewOrder"
            >{{ i18n.t('/change-order/actions/create-new-order') }}
          </app-button>
          <app-button
            v-else
            :disabled="sendOrderNadinDisabled"
            data-qa="send-change-order-button"
            @click="sendOrder"
            >{{ i18n.t('/change-order/actions/send-order') }}
          </app-button>
        </div>
      </div>
    </div>

    <change-order-modification-result
      v-if="showOrderModal"
      :is-try-order-mode="isTryOrderMode"
      :order-result="orderResult"
      :show-ifa-messages="dealerSettings.isImporter"
      @close="showOrderModal = false"
    />

    <modal
      v-if="showOrderSolutionsModal"
      class="modalPopupDialog"
      :title="'Kan de wijziging niet opslaan'"
      @close="showOrderSolutionsModal = false"
    >
      <div v-if="isResolving" class="overlay">
        <loader />
      </div>

      <p>
        {{ i18n.t('/change-order/results/solutions/title') }}
      </p>

      <div v-if="hasAddOptions">
        <h5>{{ i18n.t('/change-order/results/solutions/add-options') }}</h5>
        <div class="conflict-options-container">
          <div
            v-for="addOption in orderSolutions.AddOptions"
            :key="addOption.Code"
            class="conflict-option-row"
            :class="{ selected: selectedAddOption?.Code === addOption.Code }"
            @click="selectAddOption(addOption)"
          >
            <div class="option-info">
              <span class="option-id">{{ addOption.ShortId }}</span>
              <span class="option-description">{{ addOption.Description }}</span>
            </div>
            <app-button
              data-qa="add_option"
              class="conflict-action-btn add-btn"
              :disabled="isResolving"
              @click.stop="onAddConflictOption(addOption)"
            >
              Toevoegen
            </app-button>
          </div>
        </div>
      </div>

      <div v-if="hasRemoveOptions">
        <h5>{{ i18n.t('/change-order/results/solutions/remove-options') }}</h5>
        <div class="conflict-options-container">
          <div
            v-for="removeOption in orderSolutions.RemoveOptions"
            :key="removeOption.Code"
            class="conflict-option-row"
            :class="{ selected: selectedRemoveOption?.Code === removeOption.Code }"
            @click="selectRemoveOption(removeOption)"
          >
            <div class="option-info">
              <span class="option-id">{{ removeOption.ShortId }}</span>
              <span class="option-description">{{ removeOption.Description }}</span>
            </div>
            <app-button
              data-qa="remove_option"
              class="conflict-action-btn remove-btn"
              :disabled="isResolving"
              @click.stop="onRemoveConflictOption(removeOption)"
            >
              Verwijderen
            </app-button>
          </div>
        </div>
      </div>

      <app-button data-qa="close-order-modal-button" @click="showOrderSolutionsModal = false"
        >{{ i18n.t('/Common/Close') }}
      </app-button>
    </modal>

    <modal
      v-if="showConfirmationModal"
      class="modalPopupDialog"
      :title="i18n.t('/change-order/actions/send-order')"
      max-width="50%"
      @close="showConfirmationModal = false"
    >
      <div class="actions-container">
        <span class="actions-subtitle">
          {{ i18n.t('/change-order/sections/changed-options') }}
        </span>
        <div class="changes-overview">
          <div :class="getStatusItemClass(carConfigurationStatus)">
            <i :class="getStatusIconClass(carConfigurationStatus)"></i>
            <span>{{ i18n.t('/change-order/sections/configuration') }}</span>
            <div class="change-group">
              <div class="change-group-title">Toegevoegd</div>
              <div class="change-group-list">
                <div
                  v-for="item in getChangedItems().added"
                  :key="item.Id"
                  class="change-row added"
                >
                  <div class="item-text">{{ item.Name }}</div>
                  <div class="item-price">{{ formatInclPrice(item.Price.Incl) }}</div>
                </div>
              </div>
            </div>

            <div class="change-group">
              <div class="change-group-title">Verwijderd</div>
              <div class="change-group-list">
                <div
                  v-for="item in getChangedItems().removed"
                  :key="item.Id"
                  class="change-row removed"
                >
                  <div class="item-text">{{ item.Name }}</div>
                  <div class="item-price">{{ formatInclPrice(item.Price.Incl) }}</div>
                </div>
              </div>
            </div>

            <div class="change-group price-impact">
              <div class="change-group-title">Totaal optie wijzigingen</div>
              <div class="change-group-list">
                <div class="change-row">
                  <div class="item-text">Netto prijsimpact</div>
                  <div class="item-price">{{ getFormattedPriceImpact() }}</div>
                </div>
              </div>
            </div>
          </div>

          <div :class="getStatusItemClass(deliveryDateStatus)">
            <i :class="getStatusIconClass(deliveryDateStatus)"></i>
            <span>{{ i18n.t('/change-order/sections/delivery-dates') }}</span>
          </div>

          <!-- Show additional status items only for generic (importer) flow -->
          <template v-if="!hasQuoteId">
            <div :class="getStatusItemClass(dealerAndLocationStatus)">
              <i :class="getStatusIconClass(dealerAndLocationStatus)"></i>
              <span>{{ i18n.t('/change-order/sections/dealer-and-location') }}</span>
            </div>
            <div v-if="dealerSettings.isImporter" :class="getStatusItemClass(customerStatus)">
              <i :class="getStatusIconClass(customerStatus)"></i>
              <span>{{ i18n.t('/change-order/sections/customer-data') }}</span>
            </div>
            <div v-if="dealerSettings.isImporter" :class="getStatusItemClass(ReservationStatus)">
              <i :class="getStatusIconClass(ReservationStatus)"></i>
              <span>{{ i18n.t('/change-order/sections/reservation') }}</span>
            </div>
            <div v-if="dealerSettings.isImporter" :class="getStatusItemClass(LicensePlateStatus)">
              <i :class="getStatusIconClass(LicensePlateStatus)"></i>
              <span>{{ i18n.t('/change-order/sections/license-plate') }}</span>
            </div>
            <div v-if="dealerSettings.isImporter" :class="getStatusItemClass(shouldDeleteOrder)">
              <i :class="getStatusIconClass(shouldDeleteOrder)"></i>
              <span>{{ i18n.t('/change-order/sections/delete-order') }}</span>
            </div>
          </template>
        </div>
      </div>
      <div class="button-panel">
        <app-button data-qa="cancel-change-order-button" @click="cancelSendOrder">
          {{ i18n.t('/common/cancel') }}
        </app-button>

        <app-button
          data-qa="confirm-change-order-button"
          :disabled="submitOrderDisabled"
          @click="executeSendOrder"
        >
          {{ i18n.t('/change-order/actions/send-order') }}
        </app-button>
      </div>
    </modal>

    <!-- Request signing modal - only for quote flow (dealer) -->
    <modal
      v-if="showRequestChangeOrderSigningModal"
      class="modalPopupDialog"
      :title="printPdfDialogTitle"
      @close="handleRequestChangeOrderSigningModalClose"
    >
      <request-change-order-form @close="handleFormSubmitted" />
    </modal>
  </div>
</template>

<script>
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'
import changeOrderService from '@/services/changeOrderService'
import { mapGetters, mapState } from 'vuex'
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'
import ChangeOrderStatus from '@/helpers/enums/changeOrderStatus'
import ChangeOrderModificationResult from '@/components/changeOrder/presentational/organisms/changeOrderActions/ChangeOrderModificationResult.vue'
import RequestChangeOrderForm from '@/components/changeOrder/presentational/organisms/changeOrderActions/RequestChangeOrderForm.vue'
import ChangeOrderStatusProgress from '@/components/changeOrder/presentational/molecules/ChangeOrderProgress/ChangeOrderStatusProgress.vue'

export default {
  components: {
    Loader,
    Modal,
    AppButton,
    ChangeOrderModificationResult,
    RequestChangeOrderForm,
    ChangeOrderStatusProgress,
  },
  data() {
    return {
      orderResult: {
        ModifyCarConfigurationResult: {
          MessageID: '',
          ErrorMessages: null,
          IfaStatus: '',
          IfaErrors: null,
          ProductCatalogueSeverity: '',
          ProductCatalogueMessages: null,
          Status: '',
        },
        ModifyEstablishmentResult: {
          ErrorMessages: null,
        },
        ModifyAdditionalInfoResult: {
          ErrorMessages: null,
        },
        ModifyUnloadingLocationResult: {
          ErrorMessages: null,
        },
        ModifyDeliveryDateResult: {
          ErrorMessages: null,
        },
        ModifyCustomerResult: {
          ErrorMessages: null,
        },
        RequestLicensePlateNumberResult: {
          ErrorMessages: null,
        },
        ModifyLicensePlateTypeResult: {
          ErrorMessages: null,
        },
        ModifyTaxResult: {
          ErrorMessages: null,
        },
        ModifyReservationResult: {
          ErrorMessages: null,
        },
        ModifyCommercialHoldResult: {
          ErrorMessages: null,
        },
        ModifyHoldShowRoomResult: {
          ErrorMessages: null,
        },
        ModifyHoldStockResult: {
          ErrorMessages: null,
        },
        ModifyTransportHoldResult: {
          ErrorMessages: null,
        },
        DeleteOrderResult: {
          ErrorMessages: null,
        },
      },
      canOrder: false,
      showOrderModal: false,
      showOrderSolutionsModal: false,
      orderSolutions: {
        AddOptions: [],
        RemoveOptions: [],
      },
      orderMode: '',
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      showConfirmationModal: false,
      carConfigurationStatus: '',
      deliveryDateStatus: '',
      dealerAndLocationStatus: '',
      customerStatus: '',
      ReservationStatus: '',
      LicensePlateStatus: '',
      shouldDeleteOrder: false,
      isLoading: false,
      selectedAddOption: null,
      selectedRemoveOption: null,
      isResolving: false,
      showRequestChangeOrderSigningModal: false,
    }
  },
  computed: {
    ...mapState('changeOrder', ['changeOrder', 'remarkChanged']),
    ...mapGetters({
      isStatusConcept: 'changeOrder/changeOrderStatusIsConcept',
      isSigningStatusPending: 'changeOrder/changeOrderSigningStatusIsPending',
      isSigningStatusCompleted: 'changeOrder/changeOrderSigningStatusIsCompleted',
      isChangeOrderStatusSubmitted: 'changeOrder/changeOrderStatusIsSubmitted',
      canPreviewChangeOrder: 'user/canPreviewChangeOrder',
      canModifyChangeOrder: 'user/canModifyChangeOrder',
      dealerSettings: 'user/dealerSettings',
      sidebarOpen: 'ui/sidebarOpen',
      sidebarWidth: 'ui/sidebarWidth',
    }),
    navBarStyle() {
      return {
        left: this.sidebarOpen ? `${this.sidebarWidth}px` : '0',
        transition: 'left 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
      }
    },
    hasQuoteId() {
      return !!this.changeOrder?.QuoteId
    },
    isAudiExclusiveBlocked() {
      return this.changeOrder?.AudiExclusiveNotice === 'BLOCKED'
    },
    // Quote flow specific computed properties
    canShowTryOrderButton() {
      if (!this.hasQuoteId) return false
      return (
        this.isStatusConcept &&
        !this.hasSuccessTryOrderResult &&
        !this.isSigningStatusPending &&
        !this.isSigningStatusCompleted &&
        !this.isChangeOrderStatusSubmitted
      )
    },
    canShowPDFButton() {
      if (!this.hasQuoteId) return false
      return this.isStatusConcept && this.hasSuccessTryOrderResult && !this.isSigningStatusCompleted
    },
    canShowChangeOrderButton() {
      if (!this.hasQuoteId) return false
      return this.isStatusConcept && this.isSigningStatusCompleted && this.hasSuccessTryOrderResult
    },
    hasSuccessTryOrderResult() {
      return this.changeOrder?.ValidationMessages?.ModifyCarConfigurationResult?.Status === 'OK'
    },
    printPdfDialogTitle() {
      return this.i18n.t('/change-order/forms/print-pdf-title')
    },
    // Generic flow specific computed properties
    orderIsSubmitted() {
      return this.changeOrder.MainOrderStatus === ChangeOrderStatus.submitted
    },
    isSaveButtonDisabled() {
      return !this.hasChangeOrderChanges
    },
    // Shared computed properties
    hasAddOptions() {
      return this.orderSolutions.AddOptions != null
    },
    hasRemoveOptions() {
      return this.orderSolutions.RemoveOptions != null
    },
    carConfigurationChangesSubmittedSuccessful() {
      return this.orderResult.ModifyCarConfigurationResult?.Status === 'OK'
    },
    hasChangeOrderChanges() {
      if (
        this.changeOrder.MainOrderStatus === ChangeOrderStatus.submitted ||
        this.changeOrder.MainOrderStatus === ChangeOrderStatus.archived
      ) {
        return false
      }

      // For quote flow, only check car configuration
      if (this.hasQuoteId) {
        return this.changeOrder.CarConfiguration.OrderStatus === 'CHANGED'
      }

      // For generic flow, check all fields
      return (
        this.changeOrder.CarConfiguration.OrderStatus === 'CHANGED' ||
        this.changeOrder.OwningDealer.OrderStatus === 'CHANGED' ||
        this.changeOrder.AdditionalInfo.OrderStatus === 'CHANGED' ||
        this.changeOrder.DeliveryDate.OrderStatus === 'CHANGED' ||
        this.changeOrder.Customer.OrderStatus === 'CHANGED' ||
        this.changeOrder.Reservation.OrderStatus === 'CHANGED' ||
        this.changeOrder.Holds.CommercialHold.OrderStatus === 'CHANGED' ||
        this.changeOrder.Holds.ShowroomHold.OrderStatus === 'CHANGED' ||
        this.changeOrder.Holds.StockHold.OrderStatus === 'CHANGED' ||
        this.changeOrder.Holds.TransportHold.OrderStatus === 'CHANGED' ||
        this.changeOrder.UnloadingLocation.OrderStatus === 'CHANGED' ||
        this.changeOrder.LicensePlates.OrderStatus === 'CHANGED' ||
        this.changeOrder.TaxFree.OrderStatus === 'CHANGED' ||
        this.remarkChanged ||
        this.changeOrder.IsCanDeleteOrder === true
      )
    },
    tryOrderDisabled() {
      if (this.isAudiExclusiveBlocked) {
        return true
      }
      if (!this.canPreviewChangeOrder) {
        return true
      }

      if (
        this.changeOrder.MainOrderStatus === ChangeOrderStatus.submitted ||
        this.changeOrder.MainOrderStatus === ChangeOrderStatus.archived
      ) {
        return true
      }
      if (
        this.changeOrder.CarConfiguration.OrderStatus === 'CHANGED' &&
        this.changeOrder.CarConfiguration.ValidStatus?.IsValid
      ) {
        return false
      }

      return true
    },
    submitOrderDisabled() {
      if (this.isAudiExclusiveBlocked) {
        return true
      }
      return !this.canModifyChangeOrder
    },
    sendOrderNadinDisabled() {
      if (this.isAudiExclusiveBlocked) {
        return true
      }
      if (!this.canModifyChangeOrder) {
        return true
      }
      return !this.hasChangeOrderChanges
    },
    isTryOrderMode() {
      return this.orderMode === 'tryOrder'
    },
  },
  methods: {
    // Quote flow specific methods
    handleRequestChangeOrderSigningModalClose() {
      this.showRequestChangeOrderSigningModal = false
    },
    async handleFormSubmitted() {
      this.showRequestChangeOrderSigningModal = false
    },
    async showPrintOrderDialogue() {
      this.showRequestChangeOrderSigningModal = true
    },
    // Generic flow specific methods
    createNewOrder() {
      let params = {
        establishmentId: this.$route?.params?.establishmentId,
        komNumber: this.changeOrder.KomNumber,
        komYear: this.changeOrder.KomYear,
      }

      this.$router.push({ name: 'new-change-order-page', params, force: true })
    },
    async saveChangeOrder() {
      this.isLoading = true
      await this.executeSaveOperations()
      this.isLoading = false
    },
    getStatusIconClass(status) {
      return `fas ${status === 'CHANGED' ? 'fa-check' : 'fa-minus'}`
    },
    // Shared methods
    async executeSendOrder() {
      this.isLoading = true
      this.showConfirmationModal = false
      this.orderMode = 'sendOrder'

      try {
        this.orderResult = await changeOrderService.submitChangeOrder(
          this.changeOrder?.ChangeOrderId
        )
        this.canOrder = this.orderResult.Status === 'OK'
      } catch (error) {
        this.orderResult = {
          Status: 'ERROR',
          Error: error?.response?.data?.ErrorDescription || 'Onverwacht resultaat',
        }
        this.canOrder = false
      } finally {
        await changeOrderService.getChangeOrderByChangeOrderId(this.changeOrder?.ChangeOrderId)
      }
      this.isLoading = false

      this.showOrderModal = true
    },
    async executeSaveOperations() {
      if (!this.changeOrder.CarConfiguration.HasUnknownOkapiModel) {
        if (this.changeOrder.FactoryStatusNumber <= 280 && !this.hasOnlyImporterOptions()) {
          try {
            this.orderSolutions = await changeOrderService.verifyOrder()
          } catch (error) {
            // Verification failed: saving is not possible
            this.orderSolutions = {}
            this.isLoading = false
            this.showOrderSolutionsModal = true
            return false
          }

          if (
            this.orderSolutions?.AddOptions?.length ||
            this.orderSolutions?.RemoveOptions?.length
          ) {
            this.isLoading = false
            this.showOrderSolutionsModal = true
            return false
          }
        }
      }
      if (this.$route.name === 'new-change-order-page') {
        const result = await changeOrderService.createChangeOrder()
        let params = {
          changeOrderId: result.ChangeOrderId,
          establishmentId: this.$route.params.establishmentId,
        }
        this.$router.push({ name: 'change-order-page', params })
      } else {
        await changeOrderService.updateChangeOrder()
      }
      return true
    },
    hasOnlyImporterOptions() {
      const findDifferences = (original, changed) => {
        const onlyInOriginal = original.filter(
          (originalItem) => !changed.some((changedItem) => changedItem.Id === originalItem.Id)
        )

        const onlyInChanged = changed.filter(
          (changedItem) => !original.some((originalItem) => originalItem.Id === changedItem.Id)
        )

        return [...onlyInOriginal, ...onlyInChanged]
      }

      const isImporterOption = (option) => {
        return option.Type === 'GENERAL_OPTION' && option.Classification === 'LOCAL_EQUIPMENT'
      }

      let originalOptions = this.changeOrder.CarConfiguration.OriginalOptions
      let changedOptions = this.changeOrder.CarConfiguration.ChangedOptions

      const differences = findDifferences(originalOptions, changedOptions)
      const nonImporterOptions = differences.filter((option) => !isImporterOption(option))

      return nonImporterOptions.length === 0
    },
    async executeTryOrderOperations() {
      this.orderMode = 'tryOrder'
      try {
        this.orderResult = await changeOrderService.tryOrder(this.changeOrder?.ChangeOrderId)

        // For quote flow, store validation messages
        if (this.hasQuoteId) {
          this.changeOrder.ValidationMessages = this.orderResult
        }

        this.canOrder = this.carConfigurationChangesSubmittedSuccessful
      } catch (error) {
        this.canOrder = false
        this.orderResult = {
          Status: 'Error',
          Error: error?.response?.data?.ErrorDescription || 'Onverwacht resultaat',
        }
      }
      this.showOrderModal = true
    },
    showAllChangesPopUp() {
      this.carConfigurationStatus = this.changeOrder.CarConfiguration.OrderStatus
      this.deliveryDateStatus = this.changeOrder.DeliveryDate.OrderStatus

      // Only set these for generic flow
      if (!this.hasQuoteId) {
        this.dealerAndLocationStatus =
          this.changeOrder.OwningDealer.OrderStatus === 'CHANGED' ||
          this.changeOrder.UnloadingLocation.OrderStatus === 'CHANGED' ||
          this.changeOrder.AdditionalInfo.OrderStatus === 'CHANGED' ||
          this.changeOrder.Holds.TransportHold?.OrderStatus === 'CHANGED'
            ? 'CHANGED'
            : 'ORIGINAL'

        this.customerStatus = this.changeOrder.Customer.OrderStatus

        this.ReservationStatus =
          this.changeOrder.Reservation.OrderStatus === 'CHANGED' ||
          this.changeOrder.Holds.ShowroomHold.OrderStatus === 'CHANGED' ||
          this.changeOrder.Holds.StockHold.OrderStatus === 'CHANGED'
            ? 'CHANGED'
            : 'ORIGINAL'

        this.LicensePlateStatus =
          this.changeOrder.Holds.CommercialHold.OrderStatus === 'CHANGED' ||
          this.changeOrder.LicensePlates.OrderStatus === 'CHANGED' ||
          this.changeOrder.TaxFree.OrderStatus === 'CHANGED'
            ? 'CHANGED'
            : 'ORIGINAL'

        this.shouldDeleteOrder = this.changeOrder.IsCanDeleteOrder ? 'CHANGED' : 'ORIGINAL'
      }

      this.showConfirmationModal = true
    },
    async tryOrder() {
      this.isLoading = true
      let correctlySaved = await this.executeSaveOperations()
      if (correctlySaved) {
        await this.executeTryOrderOperations()
      }
      this.isLoading = false
    },
    async sendOrder() {
      this.isLoading = true
      let correctlySaved = await this.executeSaveOperations()
      this.isLoading = false
      if (correctlySaved) {
        this.showAllChangesPopUp()
      }
    },
    cancelSendOrder() {
      this.showConfirmationModal = false
    },
    getStatusItemClass(status) {
      return `changes-item
        ${status === 'CHANGED' ? 'changes-item-changed' : 'changes-item-unchanged'}`
    },
    getFormattedPriceImpact() {
      let totalSum = this.getPriceImpact()
      if (totalSum > 0) {
        return '+ ' + this.i18n.n(totalSum, 'currency')
      } else if (totalSum < 0) {
        return '- ' + this.i18n.n(Math.abs(totalSum), 'currency')
      } else if (totalSum === 0) {
        return 'Geen verschil'
      }
    },
    getPriceImpact() {
      const changedItems = this.getChangedItems()

      const addedSum = changedItems.added.reduce(
        (sum, opt) => sum + (Number(opt.Price?.Incl) || 0),
        0
      )

      const removedSum = changedItems.removed.reduce(
        (sum, opt) => sum + (Number(opt.Price?.Incl) || 0),
        0
      )

      return addedSum - removedSum
    },
    getChangedItems() {
      const original = this.changeOrder.CarConfiguration.OriginalOptions || []
      const changed = this.changeOrder.CarConfiguration.ChangedOptions || []

      const added = changed.filter((opt) => !original.some((orig) => orig.Id === opt.Id))

      const removed = original.filter((opt) => !changed.some((chg) => chg.Id === opt.Id))

      return { added, removed }
    },
    formatInclPrice(price) {
      return Number.parseFloat(price) !== 0 ? this.i18n.n(price, 'currency') : 'Geen meerprijs'
    },
    selectAddOption(option) {
      this.selectedAddOption = option
    },
    selectRemoveOption(option) {
      this.selectedRemoveOption = option
    },
    async onAddConflictOption(option) {
      this.isResolving = true
      try {
        const changedOptions = [...this.changeOrder.CarConfiguration.ChangedOptions]

        if (!changedOptions.some((opt) => opt.Id === option.Code)) {
          const fullOption = this.changeOrder.CarConfiguration.AvailableOptions.find(
            (opt) => opt.Id === option.Code
          )

          if (fullOption) {
            changedOptions.push(fullOption)

            this.$store.commit('changeOrder/updateCarConfigurationChangedOptions', changedOptions)

            this.orderSolutions = await changeOrderService.verifyOrder()

            if (
              (!this.orderSolutions?.AddOptions?.length &&
                !this.orderSolutions?.RemoveOptions?.length) ||
              (!this.hasAddOptions && !this.hasRemoveOptions)
            ) {
              this.showOrderSolutionsModal = false
            }
          }
        }
      } catch (error) {
        console.error('Error adding conflict option:', error)
      } finally {
        this.isResolving = false
        this.selectedAddOption = null
      }
    },
    async onRemoveConflictOption(option) {
      this.isResolving = true
      try {
        const changedOptions = this.changeOrder.CarConfiguration.ChangedOptions.filter(
          (opt) => opt.Id !== option.Code
        )

        this.$store.commit('changeOrder/updateCarConfigurationChangedOptions', changedOptions)

        this.orderSolutions = await changeOrderService.verifyOrder()

        if (
          (!this.orderSolutions?.AddOptions?.length &&
            !this.orderSolutions?.RemoveOptions?.length) ||
          (!this.hasAddOptions && !this.hasRemoveOptions)
        ) {
          this.showOrderSolutionsModal = false
        }
      } catch (error) {
        console.error('Error removing conflict option:', error)
      } finally {
        this.isResolving = false
        this.selectedRemoveOption = null
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.modalPopupDialog {
  z-index: 999;
}

.overlay {
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: 999;

  .loader {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translateX(-50%);
  }
}

.change-order-action-buttons__wrapper {
  display: flex;
  justify-content: space-between;
  flex-direction: row;

  max-width: 1250px;
  margin-left: auto;
  margin-right: auto;

  div {
    padding-bottom: 0.5em;

    &.hidden {
      padding: 0;
      height: 0;
    }
  }

  column {
    flex: 1;
    display: flex;
    justify-content: space-between;
    flex-direction: column;
  }
}

.fixed-change-order-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  box-shadow: 0 -2px 5px rgba(0, 0, 0, 0.3);
  padding: 10px 0;
  z-index: 100;
}

.action-buttons {
  display: flex;
  justify-content: space-between;

  padding-left: 3em;
  padding-right: 3em;
  max-width: 1440px;

  margin-left: auto;
  margin-right: auto;
  button {
    margin-bottom: 0;
  }
}

.option-select-box {
  font-family: monospace, monospace;
  width: 100px;
  border: #000000 inset thin;
}

/* width */
.option-select-box::-webkit-scrollbar {
  width: 20px;
}

/* Track */
.option-select-box::-webkit-scrollbar-track {
  background-color: #ebebeb;
}

/* Handle */
.option-select-box::-webkit-scrollbar-thumb {
  background: #6d6d6d;
}

/* Handle on hover */
.option-select-box::-webkit-scrollbar-thumb:hover {
  background: #6d6d6d;
}

.modification-result {
  margin-left: 30px;
}

.actions-container {
  margin-bottom: 20px;

  .actions-subtitle {
    display: block;
    font-weight: 500;
    font-size: 20px;
    color: #0a0a0a;
  }

  .changes-overview {
    margin-bottom: 40px;
    margin-left: 25px;
    margin-top: 20px;
    font-size: 18px;

    .changes-item {
      margin-top: 10px;

      i {
        margin-right: 10px;
      }
    }

    .changes-item-changed {
      font-weight: bold;
      color: black;
    }

    .changes-item-unchanged {
      font-weight: normal;
      color: gray;
    }
  }
}

.button-panel {
  display: flex;
  justify-content: space-between;
  margin-left: 25%;
  margin-right: 25%;
}

.close-button {
  margin-top: 20px;
  display: flex;
}

.changed-items-overview {
  margin-left: 25px;
}

.change-group {
  font-weight: normal;
  margin-top: 10px;
  padding-left: 35px;
  width: 75%;
}

.change-group-title {
  font-weight: bold;
  border-bottom: 1px solid #000;
}

.change-group-list {
  margin: 0;
  padding: 0;

  .change-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 8px;
    gap: 10px;
  }

  .item-text {
    flex: 1;
    min-width: 0;
    word-wrap: break-word;
  }

  .item-price {
    white-space: nowrap;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 14px;
    flex-shrink: 0;
  }

  .price-impact {
    margin-top: 20px;
    border-top: 2px solid #ddd;
    padding-top: 10px;
  }
}

.conflict-options-container {
  border: 1px solid #ddd;
  border-radius: 4px;
  max-height: 300px;
  overflow-y: auto;
  margin-bottom: 20px;
}

.conflict-option-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f5f5f5;
  }

  &.selected {
    background-color: #e3f2fd;
    border-left: 4px solid #2196f3;
  }

  &:last-child {
    border-bottom: none;
  }
}

.option-info {
  display: flex;
  flex-direction: column;
  flex: 1;

  .option-id {
    font-weight: bold;
    color: #333;
    font-size: 14px;
  }

  .option-description {
    color: #666;
    font-size: 13px;
    margin-top: 2px;
  }
}

.conflict-action-btn {
  margin-left: 16px;
  min-width: 100px;
  font-size: 12px;
  padding: 6px 12px;

  &.add-btn {
    background-color: #4caf50;
    border-color: #4caf50;

    &:hover:not(:disabled) {
      background-color: #45a049;
      border-color: #45a049;
    }
  }

  &.remove-btn {
    background-color: #f44336;
    border-color: #f44336;

    &:hover:not(:disabled) {
      background-color: #da190b;
      border-color: #da190b;
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}
</style>
